import type { WorkerHistoricalResponse, HistoricalClose } from '../types.ts';

const OPTION_MARK_REQUEST_SIZE = 50;
const OPTION_MARK_BACKFILL_SYMBOL_BATCH_SIZE = 5;
const OPTION_MARK_BACKFILL_DATE_BATCH_SIZE = 10;

export class WorkerApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'WorkerApiError';
  }
}

export async function workerGet<T>(path: string): Promise<T> {
  const url = `${CONFIG.WORKER_BASE_URL}${path}`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${CONFIG.WORKER_API_KEY}` },
  });

  if (!response.ok) {
    throw new WorkerApiError(response.status, await workerErrorMessage(response));
  }

  return response.json();
}

export async function workerPost<T>(path: string, body: unknown): Promise<T> {
  const url = `${CONFIG.WORKER_BASE_URL}${path}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CONFIG.WORKER_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new WorkerApiError(response.status, await workerErrorMessage(response));
  }

  return response.json();
}

export async function getHistoricalPrices(
  symbols: string[],
  calendarDays: number,
  options?: { storage?: 'legacy' | 'ticker' }
): Promise<Record<string, HistoricalClose[]>> {
  const params = new URLSearchParams({
    symbols: symbols.join(','),
    days: calendarDays.toString(),
  });
  if (options?.storage) {
    params.set('storage', options.storage);
  }

  const response = await workerGet<WorkerHistoricalResponse>(
    `/api/prices/historical?${params}`
  );
  return response.data;
}

export async function getStoredOptionMarks(
  optionSymbols: string[],
  calendarDays: number
): Promise<Record<string, HistoricalClose[]>> {
  const results: Record<string, HistoricalClose[]> = {};

  for (const symbols of chunks(uniqueStrings(optionSymbols), OPTION_MARK_REQUEST_SIZE)) {
    const params = new URLSearchParams({
      symbols: symbols.join(','),
      days: calendarDays.toString(),
    });

    try {
      const response = await workerGet<WorkerHistoricalResponse>(
        `/api/options/marks?${params}`
      );
      Object.assign(results, response.data);
    } catch (error) {
      if (isMissingWorkerRoute(error)) {
        return {};
      }
      throw error;
    }
  }

  return results;
}

export async function storeCurrentOptionMarks(
  optionSymbols: string[]
): Promise<void> {
  if (optionSymbols.length === 0) return;

  for (const symbols of chunks(uniqueStrings(optionSymbols), OPTION_MARK_REQUEST_SIZE)) {
    try {
      await workerPost('/api/options/marks/snapshot', { symbols });
    } catch (error) {
      if (isMissingWorkerRoute(error)) {
        return;
      }
      throw error;
    }
  }
}

export async function backfillStoredOptionMarks(
  optionSymbols: string[],
  dates: string[],
  options: { includeTradeFallback?: boolean } = {}
): Promise<void> {
  const symbols = uniqueStrings(optionSymbols);
  const dateList = uniqueStrings(dates)
    .filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort();
  if (symbols.length === 0 || dateList.length === 0) return;

  for (const symbolBatch of chunks(symbols, OPTION_MARK_BACKFILL_SYMBOL_BATCH_SIZE)) {
    for (const dateBatch of chunks(dateList, OPTION_MARK_BACKFILL_DATE_BATCH_SIZE)) {
      await workerPost('/api/options/marks/backfill', {
        symbols: symbolBatch,
        dates: dateBatch,
        includeTradeFallback: options.includeTradeFallback ?? true,
      });
    }
  }
}

function isMissingWorkerRoute(error: unknown): boolean {
  return error instanceof WorkerApiError && error.status === 404;
}

async function workerErrorMessage(response: Response): Promise<string> {
  const fallback = `Worker API error: ${response.status}`;
  const text = await response.text().catch(() => '');
  if (!text) return fallback;

  try {
    const data = JSON.parse(text) as { error?: unknown };
    return typeof data.error === 'string'
      ? `${fallback}: ${data.error}`
      : fallback;
  } catch {
    return `${fallback}: ${text.slice(0, 240)}`;
  }
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function chunks<T>(values: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    result.push(values.slice(i, i + size));
  }
  return result;
}
